/**
 * Strategies: an ordered stack of spreads, played simultaneously (SPEC-V2 §3.2).
 *
 * A strategy is deliberately thin. It carries no money, no stake and no
 * stopping rules — those belong to a *test*, not to the arrangement under test,
 * and holding them here would make two strategies incomparable by construction.
 * What it does carry is order, because order is what the colour ramp and the
 * per-layer attribution are keyed on.
 *
 * The layers are simultaneous. Every one of them is on the felt every spin;
 * there is no switching, no triggering and no condition. The file that used to
 * live at this path did all three, and is gone (SPEC-V2 §10).
 *
 * Pure TypeScript, importable by the worker.
 */

import type {
  BetType,
  PlacedBet,
  Spread,
  SpreadAnalysis,
  TableProfile,
  WheelStyle,
} from './types';
import { analyseSpread, normaliseBets } from './spread';
import { betKey, betLabel, coveredPockets } from './bets';
import { getWheel } from './wheels';

/**
 * Six is where a categorical colour ramp stops being distinguishable, and a
 * stack you cannot tell apart is a stack you cannot read (SPEC-V2 §4.5).
 */
export const MAX_LAYERS = 6;

export interface StrategyLayer {
  readonly spreadId: string;
}

export interface Strategy {
  readonly id: string;
  readonly name: string;
  /** Immutable after creation, as it is for a spread. */
  readonly wheelStyle: WheelStyle;
  readonly layers: readonly StrategyLayer[];
  readonly tags: readonly string[];
  readonly createdAt: number;
  readonly updatedAt: number;
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

export function newStrategyId(): string {
  return `st_${Date.now().toString(36)}_${Math.floor(Math.random() * 0xffffff).toString(36)}`;
}

export function createStrategy(name: string, wheelStyle: WheelStyle): Strategy {
  const now = Date.now();
  return {
    id: newStrategyId(),
    name,
    wheelStyle,
    layers: [],
    tags: [],
    createdAt: now,
    updatedAt: now,
  };
}

export type AddCheck = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/**
 * Whether a spread can join this strategy, and why not when it cannot.
 *
 * The UI needs the reason as much as the verdict — a catalogue row that is
 * merely inert teaches nothing, and "already in this strategy" and "wrong
 * wheel" call for different responses from the user.
 */
export function canAddSpread(strategy: Strategy, spread: Spread): AddCheck {
  if (spread.wheelStyle !== strategy.wheelStyle) {
    return {
      ok: false,
      reason: `${getWheel(spread.wheelStyle).label} spread — this strategy is ${getWheel(
        strategy.wheelStyle,
      ).label.toLowerCase()}`,
    };
  }
  if (strategy.layers.some((layer) => layer.spreadId === spread.id)) {
    return { ok: false, reason: 'Already in this strategy' };
  }
  if (strategy.layers.length >= MAX_LAYERS) {
    return { ok: false, reason: `A strategy holds at most ${MAX_LAYERS} spreads` };
  }
  return { ok: true };
}

/**
 * Add a spread as the last layer.
 *
 * Refuses rather than throws when `canAddSpread` says no, so a double-click or
 * a stale hover cannot crash the composer. Callers show the reason from
 * `canAddSpread`; this only has to keep the strategy valid.
 */
export function addLayer(strategy: Strategy, spread: Spread): Strategy {
  if (!canAddSpread(strategy, spread).ok) return strategy;
  return {
    ...strategy,
    layers: [...strategy.layers, { spreadId: spread.id }],
    updatedAt: Date.now(),
  };
}

export function removeLayer(strategy: Strategy, spreadId: string): Strategy {
  if (!strategy.layers.some((layer) => layer.spreadId === spreadId)) return strategy;
  return {
    ...strategy,
    layers: strategy.layers.filter((layer) => layer.spreadId !== spreadId),
    updatedAt: Date.now(),
  };
}

/**
 * Move a layer within the stack. Order is display and attribution only — it
 * changes which colour a spread wears, never what is placed or what it pays.
 */
export function moveLayer(strategy: Strategy, from: number, to: number): Strategy {
  const count = strategy.layers.length;
  if (from === to || from < 0 || to < 0 || from >= count || to >= count) return strategy;
  const layers = [...strategy.layers];
  const [moved] = layers.splice(from, 1);
  if (!moved) return strategy;
  layers.splice(to, 0, moved);
  return { ...strategy, layers, updatedAt: Date.now() };
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Problems that make a strategy unusable. Empty means valid.
 *
 * Takes the spread library because a strategy references spreads by id and a
 * referenced spread can be deleted out from under it. That dangling case is
 * reported rather than silently dropped: a strategy quietly playing five of its
 * six layers would be a lie about what was tested.
 */
export function validateStrategy(strategy: Strategy, spreads: readonly Spread[]): string[] {
  const errors: string[] = [];
  const byId = new Map(spreads.map((spread) => [spread.id, spread]));

  if (!strategy.name.trim()) errors.push('Strategy needs a name');
  if (strategy.layers.length > MAX_LAYERS) {
    errors.push(`A strategy holds at most ${MAX_LAYERS} spreads`);
  }

  const seen = new Set<string>();
  for (const layer of strategy.layers) {
    if (seen.has(layer.spreadId)) {
      errors.push('A spread can only appear once in a strategy');
      break;
    }
    seen.add(layer.spreadId);
  }

  for (const layer of strategy.layers) {
    const spread = byId.get(layer.spreadId);
    if (!spread) {
      errors.push('A spread in this strategy no longer exists');
      continue;
    }
    if (spread.wheelStyle !== strategy.wheelStyle) {
      errors.push(
        `"${spread.name}" is a ${getWheel(spread.wheelStyle).label.toLowerCase()} spread`,
      );
    }
  }

  return errors;
}

// ---------------------------------------------------------------------------
// Analysis
// ---------------------------------------------------------------------------

export interface ResolvedLayer {
  /** Position in the stack. What the colour ramp is keyed on. */
  readonly index: number;
  readonly spread: Spread;
  /** This layer's own stake, so the composer can show what it contributes. */
  readonly stakeChips: number;
  readonly pocketsCovered: number;
}

export interface TargetOverlap {
  readonly betType: BetType;
  readonly target: string;
  readonly label: string;
  /** Stack indices staking this target, in order. */
  readonly layers: readonly number[];
  readonly totalChips: number;
}

export interface StrategyAnalysis {
  readonly strategy: Strategy;
  readonly layers: readonly ResolvedLayer[];
  /** Layers whose spread is missing from the library, in stack order. */
  readonly missingSpreadIds: readonly string[];
  /**
   * One bet per target with chips summed — what the house actually sees, and
   * therefore what a table limit has to be checked against (SPEC-V2 §5.3).
   */
  readonly mergedBets: readonly PlacedBet[];
  /** The whole stack analysed as though it were one spread. */
  readonly combined: SpreadAnalysis;
  /** Targets more than one layer stakes, which is where chips pile up. */
  readonly overlaps: readonly TargetOverlap[];
}

/**
 * Analyse a strategy against a table.
 *
 * The combined figures come from `analyseSpread` over the merged layout rather
 * than from anything new, so a one-layer strategy is arithmetically identical
 * to that spread on its own — which is the property that makes the pivot safe.
 *
 * Missing spreads are skipped in the arithmetic and listed separately. Counting
 * a layer that is not there would overstate coverage; hiding that it is gone
 * would overstate the result.
 */
export function analyseStrategy(
  strategy: Strategy,
  spreads: readonly Spread[],
  table?: TableProfile,
): StrategyAnalysis {
  const byId = new Map(spreads.map((spread) => [spread.id, spread]));
  const wheel = getWheel(strategy.wheelStyle);

  const layers: ResolvedLayer[] = [];
  const missingSpreadIds: string[] = [];
  const contributors = new Map<string, { bet: PlacedBet; layers: number[]; chips: number }>();

  for (const layer of strategy.layers) {
    const spread = byId.get(layer.spreadId);
    if (!spread) {
      missingSpreadIds.push(layer.spreadId);
      continue;
    }

    const index = layers.length;
    const covered = new Set(spread.bets.flatMap((bet) => coveredPockets(bet, wheel)));
    layers.push({
      index,
      spread,
      stakeChips: spread.bets.reduce((sum, bet) => sum + bet.chips, 0),
      pocketsCovered: covered.size,
    });

    for (const bet of spread.bets) {
      const key = betKey(bet);
      const existing = contributors.get(key);
      if (existing) {
        existing.chips += bet.chips;
        if (!existing.layers.includes(index)) existing.layers.push(index);
      } else {
        contributors.set(key, { bet, layers: [index], chips: bet.chips });
      }
    }
  }

  const mergedBets = normaliseBets(layers.flatMap((layer) => layer.spread.bets));
  const combined = analyseSpread(mergedBets, strategy.wheelStyle, table);

  const overlaps: TargetOverlap[] = [...contributors.values()]
    .filter((entry) => entry.layers.length > 1)
    .map((entry) => ({
      betType: entry.bet.betType,
      target: entry.bet.target,
      label: betLabel(entry.bet),
      layers: entry.layers,
      totalChips: entry.chips,
    }))
    .sort((a, b) => b.totalChips - a.totalChips || a.label.localeCompare(b.label));

  return { strategy, layers, missingSpreadIds, mergedBets, combined, overlaps };
}

/** Total stake of the whole stack, in chips. */
export function strategyStakeChips(analysis: StrategyAnalysis): number {
  return analysis.layers.reduce((sum, layer) => sum + layer.stakeChips, 0);
}
